import React, { useState, Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { 
  MagnifyingGlassIcon, 
  XMarkIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  FunnelIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { LoadingSpinner, EmptyState } from '../common';
import { useCreatePipelineRunSelection, usePipelineRunsByPipelineId } from '../../hooks/useRelease';
import type { 
  PipelineRunDto,
  PipelineRunStageSummaryDto
} from '../../types/pipelineFlow';
import type { 
  ReleaseNodePipelineRunDto,
  CreateReleaseNodePipelineRunDto
} from '../../types/release';

interface PipelineRunSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  releaseId: string;
  nodeId: string;
  nodeName: string;
  pipelineId?: string;
  pipelineDefinitionId?: number;
  pipelineName?: string;
  existingSelection?: ReleaseNodePipelineRunDto;
  onSelectionChange?: () => void;
}

export const PipelineRunSelectionModal: React.FC<PipelineRunSelectionModalProps> = ({
  isOpen,
  onClose,
  releaseId,
  nodeId,
  nodeName: _nodeName,
  pipelineId,
  pipelineDefinitionId,
  pipelineName,
  existingSelection,
  onSelectionChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPipelineRun, setSelectedPipelineRun] = useState<PipelineRunDto | null>(null);
  const [selectionReason, setSelectionReason] = useState('');
  const [productionReadyOnly, setProductionReadyOnly] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const createPipelineRunMutation = useCreatePipelineRunSelection();
  
  // Fetch pipeline runs for the specific pipeline with stage info and filters
  const {
    data: pipelineRuns = [],
    isLoading,
    error: queryError,
    isError
  } = usePipelineRunsByPipelineId(pipelineId, 25, {
    includeStages: !productionReadyOnly,
    productionReadyOnly,
  });

  const error = isError ? queryError?.message || 'Failed to load pipeline runs' : null;

  // Set existing selection when modal opens or pipeline runs are loaded
  useEffect(() => {
    if (existingSelection && pipelineRuns.length > 0) {
      const buildId = existingSelection.buildId;
      if (buildId) {
        const existingRun = pipelineRuns.find(run => run.buildId === buildId);
        if (existingRun) {
          setSelectedPipelineRun(existingRun);
          setSelectionReason(existingSelection.selectionReason || '');
        }
      }
    }
  }, [existingSelection, pipelineRuns]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedPipelineRun(null);
      setSearchTerm('');
      setSelectionReason('');
    }
  }, [isOpen]);

  // Filter pipeline runs based on search term
  const filteredPipelineRuns = pipelineRuns.filter((run) =>
    run.buildName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    run.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
    run.requestedFor.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePipelineRunSelect = (pipelineRun: PipelineRunDto) => {
    setSelectedPipelineRun(pipelineRun);
  };

  const handleAssociatePipelineRun = async () => {
    if (!selectedPipelineRun) return;
    
    try {
      // Create the pipeline run selection
      const createDto: CreateReleaseNodePipelineRunDto = {
        releaseId,
        releaseFlowNodeId: nodeId,
        buildId: selectedPipelineRun.buildId,
        buildName: selectedPipelineRun.buildName,
        status: selectedPipelineRun.status,
        result: selectedPipelineRun.result || '',
        startTime: selectedPipelineRun.startTime,
        finishTime: selectedPipelineRun.finishTime ?? undefined,
        buildUrl: selectedPipelineRun.buildUrl,
        requestedFor: selectedPipelineRun.requestedFor,
        sourceBranch: selectedPipelineRun.sourceBranch ?? undefined,
        pipelineDefinitionId: pipelineDefinitionId || 0,
        pipelineName: pipelineName || 'Unknown',
        isSelected: true,
        selectionReason: selectionReason,
        selectedBy: 'current-user' // TODO: Get from auth context
      };
      
      await createPipelineRunMutation.mutateAsync({ 
        releaseId, 
        data: createDto 
      });
      
      onSelectionChange?.();
      onClose();
    } catch (error) {
      console.error('Failed to associate pipeline run:', error);
      // Error handling is done by React Query's mutation error state
    }
  };

  const getStatusIcon = (status: string, result?: string | null) => {
    if (status === 'InProgress') {
      return <ClockIcon className="h-5 w-5 text-blue-500" />;
    }
    
    if (result === 'Succeeded') {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    }
    
    if (result === 'Failed') {
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
    }
    
    return <InformationCircleIcon className="h-5 w-5 text-gray-500" />;
  };

  const getStatusBadge = (status: string, result?: string | null) => {
    if (status === 'InProgress') {
      return 'bg-blue-100 text-blue-800';
    }
    
    if (result === 'Succeeded') {
      return 'bg-green-100 text-green-800';
    }
    
    if (result === 'Failed') {
      return 'bg-red-100 text-red-800';
    }
    
    return 'bg-gray-100 text-gray-800';
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  };

  const getStageBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'succeeded': return 'bg-green-100 text-green-700';
      case 'failed': return 'bg-red-100 text-red-700';
      case 'inprogress': return 'bg-blue-100 text-blue-700';
      case 'canceled': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-50 text-gray-400';
    }
  };

  const cleanBranchName = (branch: string | undefined) => {
    if (!branch) return null;
    return branch.replace('refs/heads/', '');
  };

  const getBranchBadgeColor = (branch: string | undefined) => {
    if (!branch) return 'bg-gray-100 text-gray-600';
    const lower = branch.toLowerCase();
    if (lower.includes('main') || lower.includes('master')) return 'bg-purple-100 text-purple-700';
    if (lower.includes('hotfix')) return 'bg-orange-100 text-orange-700';
    return 'bg-gray-100 text-gray-600';
  };

  if (!pipelineDefinitionId || !pipelineName) {
    return (
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    Pipeline Not Associated
                  </Dialog.Title>
                  <p className="text-sm text-gray-500 mb-4">
                    This node doesn't have a pipeline associated with it. Please associate a pipeline first before selecting pipeline runs.
                  </p>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={onClose}
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <div>
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Pipeline Run Selection
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-500">
                      Select a production-ready run for <span className="font-medium">{pipelineName}</span>
                    </p>
                  </div>
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="flex h-[32rem] flex-col">
                  {/* Search Bar and Filters */}
                  <div className="p-4 border-b border-gray-200 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <MagnifyingGlassIcon className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-400 ml-3" />
                        <input
                          type="text"
                          className="block w-full rounded-md border-0 py-1.5 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          placeholder="Search pipeline runs..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowFilters(!showFilters)}
                        className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                          showFilters || productionReadyOnly
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <FunnelIcon className="h-4 w-4" />
                        Filters
                        {productionReadyOnly && (
                          <span className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-xs text-white">
                            1
                          </span>
                        )}
                      </button>
                    </div>
                    
                    {showFilters && (
                      <div className="flex flex-wrap gap-4 pt-1">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={productionReadyOnly}
                            onChange={(e) => setProductionReadyOnly(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700">Production-ready only</span>
                          <span className="text-xs text-gray-400">(release branches, staging passed, prod not started)</span>
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Pipeline Runs List */}
                  <div className="flex-1 overflow-y-auto p-4">
                    {(error || createPipelineRunMutation.error) && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-700">
                          {error || createPipelineRunMutation.error?.message || 'An error occurred'}
                        </p>
                      </div>
                    )}
                    
                    {isLoading ? (
                      <div className="flex justify-center py-8">
                        <LoadingSpinner size="sm" />
                      </div>
                    ) : filteredPipelineRuns.length ? (
                      <div className="space-y-3">
                        {filteredPipelineRuns.map((pipelineRun) => {
                          const isSelected = selectedPipelineRun?.id === pipelineRun.id;
                          return (
                            <div
                              key={pipelineRun.id}
                              className={`relative flex items-start p-4 rounded-lg border transition-all cursor-pointer ${
                                isSelected 
                                  ? 'border-indigo-200 bg-indigo-50' 
                                  : 'border-gray-200 hover:border-gray-300 bg-white'
                              }`}
                              onClick={() => handlePipelineRunSelect(pipelineRun)}
                            >
                              <div className="flex-shrink-0 mr-3">
                                <input
                                  id={`pipeline-run-${pipelineRun.id}`}
                                  name="selectedPipelineRun"
                                  type="radio"
                                  checked={isSelected}
                                  onChange={() => handlePipelineRunSelect(pipelineRun)}
                                  className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <label 
                                    htmlFor={`pipeline-run-${pipelineRun.id}`}
                                    className="block text-sm font-medium text-gray-900 cursor-pointer"
                                  >
                                    {pipelineRun.buildName}
                                  </label>
                                  {getStatusIcon(pipelineRun.status, pipelineRun.result)}
                                </div>
                                
                                <div className="flex items-center mt-2 space-x-2 flex-wrap gap-y-1">
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadge(pipelineRun.status, pipelineRun.result)}`}>
                                    {pipelineRun.result || pipelineRun.status}
                                  </span>
                                  {pipelineRun.sourceBranch && (
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getBranchBadgeColor(pipelineRun.sourceBranch)}`}>
                                      {cleanBranchName(pipelineRun.sourceBranch)}
                                    </span>
                                  )}
                                  {pipelineRun.currentStage && (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                                      Stage: {pipelineRun.currentStage}
                                    </span>
                                  )}
                                </div>
                                
                                {/* Stage progress indicators */}
                                {pipelineRun.stages && pipelineRun.stages.length > 0 && (
                                  <div className="flex items-center mt-2 gap-1 flex-wrap">
                                    {pipelineRun.stages.map((stage: PipelineRunStageSummaryDto, idx: number) => (
                                      <span
                                        key={idx}
                                        title={`${stage.name}: ${stage.status}`}
                                        className={`text-[10px] px-1.5 py-0.5 rounded ${getStageBadgeColor(stage.status)}`}
                                      >
                                        {stage.name}
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {/* Link to ADO build page for runs awaiting production approval */}
                                {pipelineRun.approvalUrl && (
                                  <div className="mt-2">
                                    <a
                                      href={pipelineRun.approvalUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded transition-colors"
                                    >
                                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                      Review &amp; Approve in Azure DevOps
                                    </a>
                                  </div>
                                )}
                                
                                <div className="flex items-center mt-1.5 space-x-4">
                                  <span className="text-xs text-gray-400">
                                    Started: {formatDate(pipelineRun.startTime)}
                                  </span>
                                  {pipelineRun.finishTime && (
                                    <span className="text-xs text-gray-400">
                                      Finished: {formatDate(pipelineRun.finishTime)}
                                    </span>
                                  )}
                                </div>
                                
                                <p className="text-xs text-gray-500 mt-1">
                                  Requested by: {pipelineRun.requestedFor}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <EmptyState 
                        title="No pipeline runs found"
                        description="No pipeline runs match your search criteria."
                        icon={<MagnifyingGlassIcon className="h-12 w-12" />}
                      />
                    )}
                  </div>
                </div>

                {/* Selection Reason Input */}
                {selectedPipelineRun && (
                  <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <label htmlFor="selectionReason" className="block text-sm font-medium text-gray-700 mb-2">
                      Selection Reason (Optional)
                    </label>
                    <textarea
                      id="selectionReason"
                      value={selectionReason}
                      onChange={(e) => setSelectionReason(e.target.value)}
                      placeholder="Why was this pipeline run selected for this release?"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      rows={2}
                    />
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                  <div className="text-sm text-gray-500">
                    {selectedPipelineRun ? `Selected: ${selectedPipelineRun.buildName}` : 'No pipeline run selected'}
                  </div>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    {selectedPipelineRun && (
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={handleAssociatePipelineRun}
                        disabled={createPipelineRunMutation.isPending}
                      >
                        {createPipelineRunMutation.isPending ? 'Associating...' : 'Select Pipeline Run'}
                      </button>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PipelineRunSelectionModal;